import axios from 'axios';
import type { AnalyticsService } from './analytics.service';

/**
 * Maps an API failure to a stable analytics classification (never raw messages).
 */
export function classifyAnalyticsError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (typeof status === 'number') {
      return `HTTP_${status}`;
    }
    return 'NETWORK_ERROR';
  }
  return 'UNKNOWN_ERROR';
}

/**
 * Runs an async API operation, publishing ServicePerformance on success and ErrorEvent on failure.
 *
 * @param analytics - Central GTM publisher.
 * @param serviceName - Stable logical service name for the dataLayer payload.
 * @param operation - Promise-returning API call.
 * @returns The operation result; rethrows after tracking errors.
 */
export async function trackTimedApiCall<T>(
  analytics: AnalyticsService,
  serviceName: string,
  operation: () => Promise<T>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    analytics.trackServicePerformance(serviceName, (performance.now() - startedAt) / 1000);
    return result;
  } catch (error) {
    analytics.trackError(serviceName, classifyAnalyticsError(error));
    throw error;
  }
}
